import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { CAP, useSession } from "@/auth/session";
import { products } from "@/api/client";

import { Chip } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { Drawer } from "@/components/Drawer";
import { KV } from "@/components/KV";
import { PageHead } from "@/components/PageHead";
import { SearchInput, Select } from "@/components/inputs";
import { StatGrid } from "@/components/StatGrid";
import { StatusChip, type StatusMap } from "@/components/StatusChip";
import {
  qk, useProducts, type ProductRow,
} from "@/modules/products/data";
import { ScopeAction, ScopeField } from "@/modules/products/ScopeBar";
import { SpecEditor } from "@/modules/products/SpecEditor";

/** Products — every catalogue kind, and what hangs off it.
 *
 *  A product IS a kind (D-92). Fabric's three-row `products` table is gone;
 *  `cluster_meter` became Cluster, `connected_cluster` became Connected
 *  Cluster, and Orbit Platform was retired because it was never hardware.
 *
 *  ROW CLICK OPENS A DRAWER. Dattatray: *"Click on each line item shall open
 *  the side drawer with more info and there is lot of info to be seen on that
 *  product."* The table can only carry the counts; the drawer carries what
 *  those counts are OF — which spaces, which models, which tools — without
 *  making a person leave the list to find out.
 *
 *  THE CATEGORY IS A COLUMN NO LONGER, AND A DROPDOWN GROUPING NOW.
 *  Dattatray said both things, a few hours apart, and both were right:
 *  *"A product sub group ... Not required"* and then *"I think product
 *  categories were useful! They make dropdown of product much easier to look
 *  at!"*
 *
 *  What changed is what the category is FOR. In this table it was noise —
 *  every row repeating "Control & display" tells you nothing the row does
 *  not. In `ProductSelect` it is the opposite: thirteen flat options is a
 *  list you read, five labelled groups is a list you scan. Same column, and
 *  it earns its place in one of the two.
 */
/** The same shape every other module uses for a status word (`ModelChip`,
 *  `PartChip`, `DriverChip`): a `StatusMap` + `StatusChip`, so the colour
 *  vocabulary is one decision. A product was showing its status as a KV row
 *  while every other drawer shows it as a chip in the header. */
const PRODUCT_CHIP: StatusMap = {
  draft: { label: "DRAFT", color: "var(--warn)" },
  active: { label: "ACTIVE", color: "var(--ok)" },
  retired: { label: "RETIRED", color: "var(--t-mute)" },
};
const ProductChip = ({ st }: { st: string }) => <StatusChip st={st} map={PRODUCT_CHIP} />;

const COLS: DataCol<ProductRow>[] = [
  { key: "name", label: "Product", width: 190, render: (p) => p.name },
  { key: "kind", label: "Kind", width: 130, mono: true, render: (p) => p.kind },
  { key: "abbr", label: "Abbr", width: 70, mono: true, render: (p) => p.abbr },
  {
    key: "shape", label: "", width: 150,
    render: (p) => (
      <span style={{ display: "flex", gap: 4 }}>
        {p.is_asset && <Chip tone="ok">asset</Chip>}
        {p.is_connected && <Chip tone="info">connected</Chip>}
        {p.has_software && <Chip tone="warn">software</Chip>}
      </span>
    ),
  },
  {
    key: "models", label: "Models", width: 90,
    render: (p) => p.catalogue_versions,
  },
  { key: "spaces", label: "Spaces", width: 80, render: (p) => p.spaces },
  { key: "maps", label: "Tool maps", width: 90, render: (p) => p.permission_maps },
  { key: "files", label: "Ref files", width: 90, render: (p) => p.reference_files },
  { key: "tools", label: "Releases", width: 90, render: (p) => p.tool_releases },
  { key: "companies", label: "Companies", width: 100, render: (p) => p.companies },
];

export function ProductsPage() {
  const q = useProducts();
  const { can } = useSession();
  const mayAuthor = can(CAP.MANAGE_PRODUCTS);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<ProductRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [config, setConfig] = useState<ProductRow | null>(null);
  /** The product being CONFIGURED, shown full width on this page.
   *
   *  Dattatray, after the first trim: *"I still see entire model addition in
   *  product side drawer!"* The Models LIST had gone; what was left was the
   *  spec-field editor, which is 230 lines of groups, fields, Add group and
   *  Save spec — and its own caption says it is "the form the Asset Catalog
   *  shows when somebody adds a model". It reads as model-building because it
   *  defines what a model looks like.
   *
   *  His earlier rule — keep what has no page of its own — was right and this
   *  is its consequence, not a contradiction of it: the editors needed a HOME,
   *  and a 560px drawer was never it. They move to a full-width panel on this
   *  page, reusing the `adding` pattern, so nothing gains a nav tab. */

  const all = q.data?.products ?? [];
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((p) => p.name.toLowerCase().includes(needle)
      || p.kind.includes(needle));
  }, [all, search]);

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Products"
        title="Products"
        desc="Every catalogue kind is a product. It carries its models, its spaces, its permission map per tool, its reference files and its tool releases."
      />

      <StatGrid stats={[
        { k: "Products", v: all.length },
        { k: "With models", v: all.filter((p) => p.catalogue_versions > 0).length },
        { k: "With spaces", v: all.filter((p) => p.spaces > 0).length },
        { k: "Reference files", v: all.reduce((n, p) => n + p.reference_files, 0) },
      ]} />

      <div style={{ display: "flex", gap: 10, margin: "10px 0", alignItems: "center" }}>
        <SearchInput value={search} placeholder="Search products…"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} />
        {mayAuthor && (
          <button className={adding ? "btn ghost sm" : "btn teal sm"}
            style={{ marginLeft: "auto" }} onClick={() => setAdding(!adding)}>
            {adding ? "Cancel" : "Add product"}
          </button>
        )}
      </div>

      {adding && <AddProductRow onDone={(p) => { setAdding(false); setOpen(p); }} />}

      {config && <ProductConfig product={config} onClose={() => setConfig(null)} />}

      <DataTable rows={rows} cols={COLS} rowKey={(p) => p.kind}
        onRowClick={(p) => setOpen(p)}
        empty={<div className="panel panel-pad cap">
          {q.isLoading ? "Loading…" : "No products match."}
        </div>} />

      {open && (
        <ProductDrawer product={open} onClose={() => setOpen(null)}
          onConfigure={(p) => { setOpen(null); setConfig(p); }} />
      )}
    </div>
  );
}

/** What the table cannot carry: the things the counts are OF. */
/** The drawer carries ONLY what has no page of its own.
 *
 *  Dattatray: *"When I click on product from list a side bar opens. The side
 *  bar gives a way to add a new model in the side bar itself. Its too much for
 *  a side bar. It shall have basic info and link to required Pages and
 *  Subpages. Dont make it cluttered!"* — then, asked what belongs in it:
 *  ***"Things that are not available on subpages of the other pages."***
 *
 *  That is a rule, not a preference, and it decides every line:
 *
 *    Models            -> Asset Catalog / Product Models   REMOVED
 *    Spaces            -> Products / Spaces                REMOVED
 *    Permission map    -> Products / Permission map        REMOVED
 *    Reference files   -> Products / Reference files       REMOVED
 *    Tool updates      -> Products / Tool updates          REMOVED
 *    Spec fields       -> nowhere else                     KEPT
 *    Companies         -> nowhere else                     KEPT
 *
 *  What went was not just volume. The Models section listed every model with
 *  its version count, which is the Product Models page rendered small and
 *  worse — a second place to read the same thing, and the one you would find
 *  first. The two that stay are here because deleting them would delete the
 *  only way to reach them.
 *
 *  The links remain as NAVIGATION — one compact row, counts included, no
 *  inline content behind any of them. A count is worth carrying because it
 *  answers "is there anything there" without a click; a list is not, because
 *  the page exists.
 */
function ProductDrawer({ product, onClose, onConfigure }: {
  product: ProductRow; onClose: () => void;
  onConfigure: (p: ProductRow) => void;
}) {
  const nav = useNavigate();
  const go = (to: string) => { onClose(); nav(to); };

  /** Reference files and Tool updates are gated on `has_software` (D-95), the
   *  same rule the pages' own product pickers apply — so the drawer cannot
   *  offer a link to a page that will not accept this product. */
  // EVERY LINK CARRIES THE PRODUCT. Dattatray: *"if I click on a vehicle
  // drawer and then select spaces, I want to go into VEHICLE spaces, not just
  // the spaces subpage."* A link that lands on an empty picker has not taken
  // you anywhere — it has taken you to the place and made you say it again.
  // The target pages read `?kind=` through `useKindParam`, which is also why
  // this does not violate "no dropdown selected by default": that rule is
  // about arriving COLD, and this is a selection made one click ago.
  const q = `?kind=${encodeURIComponent(product.kind)}`;
  const links: [string, number, string][] = [
    ["Models", product.catalogue_versions, `/asset-catalog/models${q}`],
    ["Spaces", product.spaces, `/products/spaces${q}`],
    ["Permission map", product.permission_maps, `/products/permissions${q}`],
    ...(product.has_software
      ? ([["Reference files", product.reference_files, `/products/reference${q}`],
          ["Tool updates", product.tool_releases, `/products/tools${q}`]] as [string, number, string][])
      : []),
  ];

  return (
    <Drawer onClose={onClose} header={<>
      {/* The house header grammar, shared with the Registry, Drivers, Parts
          and Asset Catalog drawers: bold name, mono secondary id, status
          chip, then shape. This used to be <strong> + <code>, which is the
          same idea spelled differently and renders at a different weight. */}
      <span style={{ fontWeight: 700 }}>{product.name}</span>
      <span className="mono cap">{product.kind}</span>
      <ProductChip st={product.status} />
      {product.is_asset && <Chip tone="ok">asset</Chip>}
      {product.is_connected && <Chip tone="info">connected</Chip>}
      {product.has_software && <Chip tone="warn">software</Chip>}
    </>}>
      {/* KV is a FULL-WIDTH row (space-between, ruled underneath). These were
          in a horizontal flex-wrap, which fights that design — the label and
          value got pushed apart by whatever width each one happened to take.
          Stacked, they line up the way they do in every other drawer. */}
      <div className="section-label flush">Identity</div>
      <KV k="Abbreviation" v={product.abbr} />
      <KV k="Category" v={product.group} mono={false} />
      <KV k="Models in the catalogue" v={product.catalogue_versions} />

      <div className="section-label">Go to</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {links.map(([label, count, to]) => (
          <button key={to} className="btn ghost sm" onClick={() => go(to)}
            title={`Open ${label} for ${product.name}`}
            style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
            {label}<Chip tone="mute">{count}</Chip>
          </button>
        ))}
      </div>

      {/* The two editors are no longer HERE — they open full width on the
          page behind this drawer. The drawer is a summary and a set of
          destinations; nothing in it is 230 lines of form. */}
      <div className="section-label">Configure</div>
      <button className="btn teal sm" onClick={() => onConfigure(product)}>
        Spec fields
      </button>
    </Drawer>
  );
}




/** The product's two editors, full width, on the page rather than in a drawer.
 *
 *  Not a subpage and not a nav tab: Dattatray has cut those twice (three
 *  catalogue tabs became one, Manufacturers was retired), and a sixth Products
 *  tab for something you reach from a row would be the same mistake. It is a
 *  MODE of this page, the way "Add product" already is.
 */
function ProductConfig({ product, onClose }: {
  product: ProductRow; onClose: () => void;
}) {
  return (
    <div className="panel panel-pad" style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
        <span style={{ fontWeight: 700 }}>{product.name}</span>
        <span className="mono cap">{product.kind}</span>
        <ProductChip st={product.status} />
        <button className="btn ghost sm" style={{ marginLeft: "auto" }} onClick={onClose}>
          Close
        </button>
      </div>

      {/* COMPANIES MOVED OUT (2026-09-28). Dattatray: *"just like model
          visibility sub page, the product shall have a product visibility
          subpage."* Who may USE a product and what a product RECORDS are two
          unrelated decisions, and they were sharing one box. Products /
          Product Visibility owns the first now — the twin of Asset Catalog /
          Model Visibility, same shape, so knowing one teaches the other. */}
      <SpecEditor kind={product.kind} />
    </div>
  );
}




/** Add a product — which creates a catalogue KIND (D-92).
 *
 *  It lands as DRAFT, because the id is only half of it: until the spec
 *  editor in the drawer has fields, adding a model of this product would
 *  show a form with nothing on it. Opening the drawer straight after
 *  creating is deliberate — the next step is right there rather than
 *  something to go and find.
 *
 *  THE ID IS PERMANENT. `catalog_models.kind`, `catalog_model_versions.kind`,
 *  `parts.kind`, `assets.asset_type` and every products table point at it,
 *  and a tool sends it. The field says so rather than letting somebody find
 *  out later.
 */
function AddProductRow({ onDone }: { onDone: (p: ProductRow) => void }) {
  const qc = useQueryClient();
  const existing = useProducts();
  const [kind, setKind] = useState("");
  const [label, setLabel] = useState("");
  const [abbr, setAbbr] = useState("");
  const [group, setGroup] = useState("");
  const [isAsset, setIsAsset] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [hasSoftware, setHasSoftware] = useState(false);

  // Offer the categories that exist, so the picker stays groupable rather
  // than growing a one-product category per typo.
  const groups = Array.from(
    new Set((existing.data?.products ?? []).map((p) => p.group)));

  const add = useMutation({
    mutationFn: () => products.create({
      kind, label, abbr, group, is_asset: isAsset, is_connected: isConnected,
      has_software: hasSoftware,
    }),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: qk.products });
      onDone(p as unknown as ProductRow);
    },
  });

  const idOk = /^[a-z][a-z0-9_]{1,15}$/.test(kind);

  return (
    <div className="panel panel-pad" style={{
      display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap",
      marginBottom: 12,
    }}>
      <ScopeField label="Product id" hint="PERMANENT — tools send it; lower case">
        <SearchInput value={kind} autoFocus mono placeholder="bms_hv" style={{ width: 150 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setKind(e.target.value.toLowerCase().replace(/[\s-]+/g, "_"))} />
      </ScopeField>
      <ScopeField label="Name" hint="what a person reads">
        <SearchInput value={label} placeholder="High-voltage BMS"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)} />
      </ScopeField>
      <ScopeField label="Short code" hint="prefixes technical model names">
        <SearchInput value={abbr} mono placeholder="BMSH" style={{ width: 90 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setAbbr(e.target.value.toUpperCase())} />
      </ScopeField>
      <ScopeField label="Category" hint="groups the product picker">
        <Select value={group} onChange={(e) => setGroup(e.target.value)}>
          <option value="">Select…</option>
          {groups.map((g) => <option key={g} value={g}>{g}</option>)}
        </Select>
      </ScopeField>
      <ScopeField label="Shape" hint="an asset is enrolled; the rest are fitted">
        <span style={{ display: "flex", gap: 10, height: 30, alignItems: "center" }}>
          <label style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <input type="checkbox" checked={isAsset}
              onChange={(e) => setIsAsset(e.target.checked)} /> asset
          </label>
          <label style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <input type="checkbox" checked={isConnected}
              onChange={(e) => setIsConnected(e.target.checked)} /> connected
          </label>
        </span>
      </ScopeField>
      {/* The SERVER seeds the FOTA fields from FOTA_METHODS. The console
          sends a boolean and never names a method key — those keys are what
          `derive()` reads, so a hand-typed near-miss would save fine and
          derive nothing, and the model would silently never reach FOTA. */}
      <ScopeField label="Software"
        hint={hasSoftware
          ? `unlocks FOTA, Reference files and Tool updates · seeds ${isConnected ? "all 5" : "the 2 phone/BLE"} update routes`
          : "a product with no software has no FOTA, reference files or tool releases"}>
        <label style={{ display: "flex", gap: 4, alignItems: "center", height: 30 }}>
          <input type="checkbox" checked={hasSoftware}
            onChange={(e) => setHasSoftware(e.target.checked)} />
          has software
        </label>
      </ScopeField>
      <ScopeAction>
        <button className="btn teal sm"
          disabled={!idOk || !label.trim() || !abbr.trim() || !group || add.isPending}
          onClick={() => add.mutate()}>
          {add.isPending ? "Creating…" : "Create product"}
        </button>
      </ScopeAction>
      {add.isError && (
        <div className="panel panel-pad" style={{ flexBasis: "100%" }}>
          {(add.error as Error).message}
        </div>
      )}
    </div>
  );
}
